import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Collapse,
  TextField,
  IconButton,
  Backdrop,
  CircularProgress,
  Skeleton,
  TablePagination,
  Avatar
} from "@mui/material";
import { useEffect, useState } from "react";
import axios from "axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from '@mui/icons-material/Close';
import ToastMessage from "./toast-message";

function EditorialBoard() {
  const [reviewers, setReviewers] = useState([]);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [selectedPeers, setSelectedPeers] = useState([]);
  const [docsLink, setDocsLink] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [actionStatus, setActionStatus] = useState({ type: 0, message: "" });
  const [updating, setUpdating] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [reviewersPerPage] = useState(10); // Fixed at 10 per page

  const clearToastMessage = () => setActionStatus({ type: 0, message: "" });

  useEffect(() => {
    const fetchReviewers = async () => {
      setFetching(true);
      try {
        const response = await axios.get(`https://api.wajls.com/admin/v1/get_editorial_members.php?status=1`);
        
        setReviewers(response.data.members || []);
      } catch (error) {
        console.error("Failed to fetch reviewers:", error);
      } finally {
        setFetching(false);
      }
    };
    fetchReviewers();
  }, []);

  const parseJson = (str) => {
    try {
      return JSON.parse(str);
    } catch {
      return {};
    }
  };

  const togglePeerSelection = (reviewer) => {
    setSelectedPeers(prev =>
      prev.find(p => p.id === reviewer.id)
        ? prev.filter(p => p.id !== reviewer.id)
        : [...prev, reviewer]
    );
  };

  const updateEditorialRole = async (id, role) => {
    try {
      setUpdating(true);

      console.log(id, role);
      
      const response = await axios.post(
        "https://api.wajls.com/admin/v1/update_editorial_role.php",
        { id, editorial_position: role },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        setReviewers(prev =>
          prev.map(r =>
            r.id === id ? { ...r, editorial_position: role } : r
          )
        );
        
        setSelectedReviewer(prev =>
          prev && prev.id === id ? { ...prev, editorial_position: role } : prev
        );

        setActionStatus({
          type: 1,
          message: "Editorial role updated successfully!",
        });
      } else {
        setActionStatus({
          type: 0,
          message: response.data.message || "Failed to update role",
        });
      }
    } catch (error) {
      console.error("Error updating editorial role:", error);
      setActionStatus({
        type: 0,
        message: "Server error while updating role",
      });
    } finally {
      setUpdating(false);
    }
  };


  const handleDelete = async () => {
    setDetailsOpen(false);
    setConfirmDialog({ open: false, action: '' });
    setLoading(true);

    try {
      const response = await axios.post("https://api.wajls.com/admin/v1/delete_editorial_board_member.php", {
        id: selectedReviewer.id
      });

      setActionStatus({
        type: response.data.success ? 1 : 0,
        message: response.data.message
      });

      if (response.data.success) {
        setReviewers(prev => prev.filter(r => r.id !== selectedReviewer.id));
        setSelectedPeers(prev => prev.filter(p => p.id !== selectedReviewer.id));
      }

    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setConfirmDialog({ open: false });
      setSelectedReviewer(null);
      setLoading(false);
    }
  };

  const handleSendToPeers = async () => {
    if (!urlInput || !docsLink) {
      setActionStatus({
        type: 0,
        message: 'Please fill in all fields'
      });
      return;
    }
  
    // Prepare the data to be sent to the backend
    const reviewersData = selectedPeers.map((reviewer) => {
      const personal = parseJson(reviewer.personal_information);
      return {
        name: personal.name,
        email: personal.email,
      };
    });
  
    const requestData = {
      reviewers: reviewersData, // The list of reviewers
      reviewLink: urlInput, // Review link from input
      googleDocsLink: docsLink, // Google Docs link from input
    };
  
    try {
      setLoading(true);

      await fetch('https://api.wajls.com/admin/v1/send_request_mail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
  
      setActionStatus({
        type: 1,
        message: 'Emails sent successfully to all valid addresses',
      });
  
    } catch (error) {
      setActionStatus({
        type: 0,
        message: `An error occurred: ${error.message}`,
      });
    } finally {
      setLoading(false);
      setSelectedPeers([]);
      setDocsLink("");
      setUrlInput("");
    }
  };
  
  const filteredReviewers = reviewers.filter(rev => {
    const personal = parseJson(rev.personal_information);
    const academic = parseJson(rev.academic_background);
    const editorial = parseJson(rev.publication_and_editorial_experience);
    const searchText = searchQuery.toLowerCase();

    return (
      personal.name?.toLowerCase().includes(searchText) ||
      personal.email?.toLowerCase().includes(searchText) ||
      personal.affiliation?.toLowerCase().includes(searchText) ||
      personal.phone?.toLowerCase().includes(searchText) ||
      personal.department?.toLowerCase().includes(searchText) ||
      academic.highest_degree?.toLowerCase().includes(searchText) ||
      rev.areas_of_expertise?.toLowerCase().includes(searchText) ||
      editorial.number_of_papers_published?.toString().toLowerCase().includes(searchText) ||
      editorial.editorial_experience?.toLowerCase().includes(searchText)
    );
  });

  // Handle page change for pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Calculate paginated data
  const paginatedReviewers = filteredReviewers.slice(
    page * reviewersPerPage,
    page * reviewersPerPage + reviewersPerPage
  );

  return (
    <Box sx={{ mt: 4, margin: "auto" }}>
      <Typography variant="h5" gutterBottom>Approved Peer Reviewers</Typography>

      <TextField
        fullWidth
        label="Search by name, email, department, expertise..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          {fetching ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : filteredReviewers.length > 0 ? (
            <>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f4f4f4" }}>
                  <TableRow>
                    <TableCell><strong>Select</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Department</strong></TableCell>
                    <TableCell align="center"><strong>Details</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReviewers.map(rev => {
                    const personal = parseJson(rev.personal_information);
                    const isSelected = selectedPeers.some(p => p.id === rev.id);
                    return (
                      <TableRow 
                        key={rev.id} 
                        hover
                        sx={{ backgroundColor: isSelected ? "#e3f2fd" : "transparent" }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => togglePeerSelection(rev)}
                          />
                        </TableCell>
                        <TableCell>{personal.name}</TableCell>
                        <TableCell>{personal.email}</TableCell>
                        <TableCell>{personal.department}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                              setSelectedReviewer(rev);
                              setDetailsOpen(true);
                            }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={filteredReviewers.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={reviewersPerPage}
                rowsPerPageOptions={[]} // Hide rowsPerPage select
              />
            </>
          ) : (
            <Typography sx={{ p: 2 }}>No board member found</Typography>
          )}
        </Box>
      </Paper>

      {/* Reviewer Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => {setDetailsOpen(false); setSelectedReviewer(null);}} fullWidth maxWidth="sm">
        <DialogTitle>Peer Reviewer Details</DialogTitle>
        {selectedReviewer && (
          <DialogContent dividers>
            {(() => {
              const personal = parseJson(selectedReviewer.personal_information);
              const academic = parseJson(selectedReviewer.academic_background);
              const editorial = parseJson(selectedReviewer.publication_and_editorial_experience);
              return (
                <>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar
                      src={personal.profile_image}
                      alt={personal.name}
                      sx={{ width: 80, height: 80 }}
                    />
                    <Box>
                      <Typography variant="h6">{personal.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {personal.title} at {personal.affiliation}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography>Name: {personal.name}</Typography>
                  <Typography>Affiliation: {personal.affiliation}</Typography>
                  <Typography>Department: {personal.department}</Typography>
                  <Typography>Email: {personal.email}</Typography>
                  <Typography>Phone: {personal.phone}</Typography>

                   <Box mt={1}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Editorial Role
                    </Typography>
                    <TextField
                      select
                      size="small"
                      value={selectedReviewer.editorial_position || ""}
                      onChange={(e) => updateEditorialRole(selectedReviewer.id, e.target.value)}
                        SelectProps={{ native: true }}
                        sx={{ minWidth: 200 }}
                      >
                      <option value="Editorial Member">Editorial Member</option>
                      <option value="Editor-in-Chief">Editor-in-Chief</option>
                      <option value="Associate Editor">Associate Editor</option>
                      <option value="Editorial Assistant">Editorial Assistant</option>
                    </TextField>
                  </Box>

                  <Box my={2}><hr /></Box>

                  <Typography>Highest Degree: {academic.highest_degree}</Typography>
                  <Typography>Expertise: {academic.areas_of_expertise}</Typography>

                  <Box my={2}><hr /></Box>

                  <Typography>Papers Published: {editorial.number_of_papers_published}</Typography>
                  <Typography>Editorial Experience: {editorial.editorial_experience || 'None'}</Typography>
                </>
              );
            })()}
          </DialogContent>
        )}
        <DialogActions>
          {updating && <CircularProgress size={14} color="inherit"/>}
          <Button onClick={() => {setDetailsOpen(false); setSelectedReviewer(null);}} color="error">Close</Button>
          <Button onClick={() => setConfirmDialog({ open: true })} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false })}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this peer reviewer?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Yes, Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Collapsible Card */}
      {/* <Collapse in={selectedPeers.length > 0}>
        <Box sx={{ position: 'fixed', bottom: 20, right: 20, width: 320, bgcolor: '#fff', boxShadow: 4, p: 2, borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1">Selected Peers</Typography>
            <IconButton size="small" onClick={() => setSelectedPeers([])}><CloseIcon /></IconButton>
          </Box>
          <Box maxHeight={200} overflow="auto" mt={1}>
            {selectedPeers.map(peer => {
              const personal = parseJson(peer.personal_information);
              return (
                <Typography key={peer.id} variant="body2">
                  {personal.name} - {personal.email}
                </Typography>
              );
            })}
          </Box>
          <TextField
            fullWidth
            required
            label="article link"
            value={docsLink}
            onChange={(e) => setDocsLink(e.target.value)}
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            required
            label="review feedback link"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Button fullWidth variant="contained" color="primary" onClick={handleSendToPeers} sx={{ mt: 1 }}>
            Send to Peers
          </Button>
        </Box>
      </Collapse> */}

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || fetching}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {actionStatus.message && (
        <ToastMessage
          type={actionStatus.type}
          message={actionStatus.message}
          onClose={clearToastMessage}
        />
      )}
      
    </Box>
  );
}

export default EditorialBoard;